import { useCallback, useEffect, useState } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateModelLabels, listGenerateModels, quoteGeneratorModel, toModelInput } from '@/api/generate.api';
import { useDeepEqualMemo } from '@/hooks/useDeepEqualMemo';
import type { GenerateModel } from '@/api/generate.api';

/**
 * The quote checks required inputs, and the prompt is one. The price doesn't depend on the prompt text, so a
 * stand-in lets the price show before anything is typed, and typing doesn't ask again.
 */
const QUOTE_PROMPT = 'price';
/** A slider changes its value while it moves, so the price is asked once it stops. */
const PRICE_DEBOUNCE_MS = 300;

/**
 * The catalog behind the model menu. It is loaded once and kept for the visit, because the node definitions
 * it reads are large and only change when a model is added. Until it arrives, the models show with their
 * name and logo but no settings. Signed-out visitors get models without params.
 */
export const generateModelsQueryOptions = (signedIn: boolean) =>
  queryOptions({
    queryKey: ['generate-models', signedIn],
    queryFn: () => listGenerateModels(signedIn),
    staleTime: Infinity,
    placeholderData: generateModelLabels,
  });

export function useGenerateModelsQuery(signedIn: boolean) {
  return useQuery(generateModelsQueryOptions(signedIn));
}

/** The model whose slug `?model=` carries, read from the catalog. Null when the catalog doesn't have it. */
export function useGenerateModelQuery(modelSlug: string | null, signedIn: boolean) {
  return useQuery({
    ...generateModelsQueryOptions(signedIn),
    select: (models: readonly GenerateModel[]) => models.find((model) => model.slug === modelSlug) ?? null,
  });
}

type GeneratePriceRequest = { modelId: string; input: Readonly<Record<string, unknown>> };

/** What one output costs, or null when the server can't price it. */
const generatePriceQueryOptions = ({ modelId, input }: GeneratePriceRequest) =>
  queryOptions({
    queryKey: ['generate-price', modelId, input],
    queryFn: async ({ signal }): Promise<number | null> => {
      const { data } = await quoteGeneratorModel(modelId, toModelInput(input, QUOTE_PROMPT), signal);
      return 'cost' in data && typeof data.cost === 'number' ? data.cost : null;
    },
    staleTime: 60_000,
    retry: false,
  });

/**
 * The server's price for one output of `request`, the run input without the prompt. Null `request` asks
 * nothing, such as when signed out. While a change is waiting to be priced, `unitCost` is undefined, so an
 * old price never shows for new settings.
 */
export function useGeneratePrice(request: GeneratePriceRequest | null) {
  const queryClient = useQueryClient();
  const current = useDeepEqualMemo(request);
  const [settled, setSettled] = useState(current);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(current), PRICE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [current]);
  const query = useQuery({
    ...generatePriceQueryOptions(settled ?? { modelId: '', input: {} }),
    enabled: Boolean(settled?.modelId),
  });
  const settling = settled !== current;

  /** The price for the settings as they are now, without the wait. Null when it can't be found. */
  const fetchUnitCost = useCallback(async (): Promise<number | null> => {
    if (!current?.modelId) {
      return null;
    }
    return queryClient.fetchQuery(generatePriceQueryOptions(current)).catch(() => null);
  }, [current, queryClient]);

  return {
    unitCost: settling ? undefined : (query.data ?? undefined),
    isLoading: Boolean(current?.modelId) && (settling || query.isPending),
    fetchUnitCost,
  };
}
